if (!customElements.get('n-product')) {
  customElements.define('n-product', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-variant-input]');
      this.variants = JSON.parse(this.querySelector('[data-product-variants]')?.textContent || '[]');
      this.current = this.variants.find(v => String(v.id) === this.input?.value);
      this.messages = JSON.parse(this.querySelector('[data-product-messages]')?.textContent || '{}');
      this.bundleData = JSON.parse(this.querySelector('[data-bundle-configuration]')?.textContent || 'null');
      this.activeBundle = null;
      this.pending = false;
      this.cartUncertain = false;
      this.classList.add('n-product--ready');
      this.querySelector('.n-product__choices')?.removeAttribute('hidden');
      this.querySelectorAll('[data-variant-choice]').forEach(input => input.addEventListener('change', () => this.selectVariant(input.value), options));
      this.input?.addEventListener('change', () => this.selectVariant(this.input.value), options);
      this.querySelectorAll('[data-thumb]').forEach(button => button.addEventListener('click', () => this.showMedia(button.dataset.thumb), options));
      this.querySelector('[data-dismiss-endorsement]')?.addEventListener('click', () => this.querySelector('[data-endorsement]')?.setAttribute('hidden', ''), options);
      for (const direction of ['prev', 'next']) this.querySelector(`[data-gallery-${direction}]`)?.addEventListener('click', () => {
        const media = [...this.querySelectorAll('[data-media-id]')];
        const index = media.findIndex(item => !item.hidden);
        const next = (index + (direction === 'next' ? 1 : -1) + media.length) % media.length;
        if (media[next]) this.showMedia(media[next].dataset.mediaId);
      }, options);
      this.querySelectorAll('[data-zoom]').forEach(button => button.addEventListener('click', () => {
        const source = button.querySelector('img');
        const target = this.querySelector('[data-zoom-image]');
        if (!source || !target) return;
        target.hidden = false;
        target.src = source.currentSrc || source.src;
        target.alt = source.alt;
        this.querySelector('dialog')?.showModal();
      }, options));
      this.querySelector('[data-zoom-close]')?.addEventListener('click', () => this.querySelector('dialog').close(), options);
      this.querySelector('dialog')?.addEventListener('click', event => {
        if (event.target === event.currentTarget) {
          const rect = event.currentTarget.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) event.currentTarget.close();
        }
      }, options);
      this.form?.addEventListener('submit', event => this.submit(event), options);
      if (this.dataset.bundlesReady === 'true' && this.bundleData?.a && this.bundleData?.b && this.querySelector('[data-bundle-selector]')) {
        this.querySelector('[data-bundle-selector]')?.removeAttribute('hidden');
        this.querySelector('[data-single-options]')?.setAttribute('hidden', '');
        this.querySelectorAll('[data-bundle-choice]').forEach(control => control.addEventListener('change', () => this.selectBundle(control.value), options));
        this.querySelectorAll('[data-bundle-option]').forEach(control => control.addEventListener('change', () => {
          this.selectBundle(control.dataset.bundleKey);
        }, options));
        this.querySelector('[data-product-quantity]')?.addEventListener('change', () => this.selectBundle(this.activeBundle, false, true), options);
        this.selectBundle(this.offerFromUrl(), false);
      }

      window.addEventListener('popstate', () => {
        const id = new URL(window.location.href).searchParams.get('variant');
        if (id) this.selectVariant(id, false);
        if (this.activeBundle) this.selectBundle(this.offerFromUrl(), false);
      }, options);
      this.buyButton = this.querySelector('form [data-product-submit]');
      this.sticky = this.querySelector('[data-product-sticky]');
      this.stickyExclusions = new Set();
      if (this.buyButton && this.sticky && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(() => this.updateSticky(), {threshold: [0, 1]});
        this.observer.observe(this.buyButton);
        this.exclusionObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => entry.isIntersecting ? this.stickyExclusions.add(entry.target) : this.stickyExclusions.delete(entry.target));
          this.updateSticky();
        }, {threshold: 0});
        document.querySelectorAll('.n-footer, .n-newsletter').forEach(section => this.exclusionObserver.observe(section));
        window.addEventListener('resize', () => this.updateSticky(), options);
        window.addEventListener('scroll', () => {
          if (this.stickyFrame) return;
          this.stickyFrame = requestAnimationFrame(() => { this.stickyFrame = null; this.updateSticky(); });
        }, { ...options, passive: true });
        document.addEventListener('focusin', () => this.updateSticky(), options);
        document.addEventListener('focusout', () => requestAnimationFrame(() => this.updateSticky()), options);
        this.sticky.querySelector('[data-product-submit]')?.addEventListener('click', event => {
          if (this.sticky.dataset.action !== 'choose') return;
          event.preventDefault();
          const target = this.activeBundle ? this.querySelector('[data-bundle-selector]') : this.querySelector('.n-product__choices:not([hidden])') || this.querySelector('[data-single-options]') || this.buyButton;
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          target?.scrollIntoView({behavior: reducedMotion ? 'auto' : 'smooth', block: 'start'});
          const control = target?.querySelector('input:not(:disabled), select:not(:disabled), button:not(:disabled)');
          control?.focus({preventScroll: true});
        }, options);
        this.updateSticky();
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.exclusionObserver?.disconnect();
      if (this.stickyFrame) cancelAnimationFrame(this.stickyFrame);
      this.stickyFrame = null;
      this.querySelector('dialog[open]')?.close();
    }
    text(key, fallback = '') { return this.messages?.[key] ?? fallback; }
    offerFromUrl() {
      const requested = new URL(window.location.href).searchParams.get('offer');
      if (['a', 'b'].includes(requested) && this.querySelector(`[data-bundle-choice][value="${requested}"]`)) return requested;
      return this.querySelector('[data-bundle-choice][value="a"], [data-bundle-choice][value="b"]')?.value || 'a';
    }
    stickyTopOffset() {
      let offset = 0;
      document.querySelectorAll('.n-header-section, .n-header, .n-announcement').forEach(header => {
        const style = window.getComputedStyle(header);
        if (style.position !== 'fixed' && style.position !== 'sticky') return;
        const rect = header.getBoundingClientRect();
        const top = Number.parseFloat(style.top) || 0;
        if (rect.bottom > 0 && rect.top <= top + 1 && rect.top < window.innerHeight) offset = Math.max(offset, rect.bottom);
      });
      return offset;
    }
    paintOfferPrice(node, result) {
      if (!node) return;
      const compare = node.querySelector('[data-bundle-price-compare]');
      if (compare) { compare.textContent = result?.compare || ''; compare.hidden = !result?.onSale; }
      const value = node.querySelector('[data-bundle-price-value]');
      if (value) { value.textContent = result?.money || this.text('unavailable', 'Nicht verfügbar'); const unit = node.querySelector('[data-bundle-unit-label]'); if (unit) unit.textContent = result?.unitLabel || ''; }
      else node.textContent = result?.price || this.text('unavailable', 'Nicht verfügbar');
    }
    purchaseVariant() {
      return this.activeBundle ? this.bundleVariant : this.current;
    }
    canBuy() {
      const variant = this.purchaseVariant();
      if (this.activeBundle) return Boolean(this.dataset.bundlesReady === 'true' && variant?.available);
      return Boolean(variant?.available && !(this.dataset.requireContents === 'true' && !variant.contents?.length));
    }
    syncButtons() {
      const variant = this.purchaseVariant();
      const unavailable = this.activeBundle ? this.text('unavailable_button', 'Angebot gerade nicht verfügbar') : variant?.available ? this.text('variant_unavailable', 'Ausführung derzeit nicht bestellbar') : this.text('sold_out', 'Ausverkauft');
      this.querySelectorAll('[data-product-submit]').forEach(button => {
        button.disabled = this.pending || this.cartUncertain || !this.canBuy();
        button.textContent = this.pending ? this.text('adding', 'Wird hinzugefügt …') : this.canBuy() ? this.text('buy', 'In den Warenkorb') : unavailable;
      });
      this.querySelectorAll('[data-variant-choice], [data-variant-input], [data-product-quantity], [data-bundle-choice], [data-bundle-option]').forEach(input => {
        if (!input.closest('[data-bundle-preview]')) input.disabled = this.pending || this.cartUncertain;
      });
      this.form?.setAttribute('aria-busy', String(this.pending));
      this.updateSticky();
    }
    updateSticky() {
      if (!this.sticky || !this.buyButton) return;
      const rect = this.buyButton.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const visible = rect.top < viewportHeight && rect.bottom > 0;
      const externalField = document.activeElement?.matches('input, textarea, select, [contenteditable=true]') && !this.contains(document.activeElement);
      const excluded = Boolean(this.stickyExclusions?.size || externalField);
      const passedPurchase = rect.bottom <= this.stickyTopOffset();
      this.sticky.hidden = excluded || (mobile ? !passedPurchase : visible || rect.top > 0);
      const choose = mobile && rect.top >= viewportHeight;
      this.sticky.dataset.action = choose ? 'choose' : 'add';
      const button = this.sticky.querySelector('[data-product-submit]');
      if (!button) return;
      button.type = choose ? 'button' : 'submit';
      if (choose) {
        button.disabled = this.pending || this.cartUncertain;
        button.textContent = this.pending ? this.text('adding', 'Wird hinzugefügt …') : this.activeBundle ? this.text('choose', 'Angebot wählen') : this.text('choose_variant', 'Ausführung wählen');
      } else {
        button.disabled = this.pending || this.cartUncertain || !this.canBuy();
        const variant = this.purchaseVariant();
        button.textContent = this.pending ? this.text('adding', 'Wird hinzugefügt …') : this.canBuy() ? this.text('buy', 'In den Warenkorb') : this.activeBundle ? this.text('unavailable_button', 'Angebot gerade nicht verfügbar') : variant?.available ? this.text('variant_unavailable', 'Ausführung derzeit nicht bestellbar') : this.text('sold_out', 'Ausverkauft');
      }
    }
    bundleValues(key) {
      return [...this.querySelectorAll(`[data-bundle-option][data-bundle-key="${key}"]`)].map(input => input.value);
    }
    offerQuantity() {
      const quantity = Number(this.querySelector('[data-product-quantity]')?.value || 1);
      return Number.isSafeInteger(quantity) && quantity > 0 ? quantity : 0;
    }
    resolveBundle(key, quantity = 1) {
      if (!window.NorelviProductOffers || !this.bundleData?.[key]) return null;
      try {
        const result = window.NorelviProductOffers.resolve(this.bundleData, key, this.bundleValues(key), quantity);
        result.money = window.NorelviProductOffers.format(result.unitPrice, this.bundleData.currency, this.bundleData.locale);
        result.compare = result.onSale ? window.NorelviProductOffers.format(result.unitCompare, this.bundleData.currency, this.bundleData.locale) : '';
        result.price = result.money + (result.unitLabel ? ' ' + result.unitLabel : '');
        return result;
      } catch { return null; }
    }
    selectBundle(key, updateUrl = true, keepQuantity = false) {
      if (this.pending || this.cartUncertain || !this.bundleData?.[key]) return;
      const previous = this.activeBundle;
      this.activeBundle = key;
      const quantity = this.querySelector('[data-product-quantity]');
      if (quantity && previous !== key && !keepQuantity) { quantity.min = 1; quantity.step = 1; quantity.removeAttribute('max'); quantity.value = 1; }
      this.querySelectorAll('[data-bundle-choice]').forEach(input => { input.checked = input.value === key; });
      for (const offer of ['a', 'b']) {
        const variant = this.resolveBundle(offer);
        const card = this.querySelector(`[data-bundle-card="${offer}"]`);
        card?.classList.toggle('is-selected', offer === key);
        const options = card?.querySelector('[data-bundle-selections]');
        if (options) options.hidden = offer !== key;
        const price = this.querySelector(`[data-bundle-price="${offer}"]`);
        this.paintOfferPrice(price, variant);
        const status = this.querySelector(`[data-bundle-status="${offer}"]`);
        if (status) status.textContent = !variant ? this.text('selection_unavailable', 'Diese Auswahl ist nicht verfügbar.') : !variant.available && offer === key ? this.text('offer_unavailable', 'Das Angebot ist gerade nicht verfügbar. Bitte versuche es später erneut.') : '';
      }
      this.bundleVariant = this.resolveBundle(key, this.offerQuantity());
      const variant = this.bundleVariant;
      if (variant?.mediaId) this.showMedia(variant.mediaId);
      this.querySelectorAll('[data-product-price], [data-sticky-price]').forEach(node => { node.textContent = variant?.price || this.text('unavailable', 'Nicht verfügbar'); });
      this.querySelectorAll('[data-product-compare]').forEach(node => { node.textContent = variant?.compare || ''; node.hidden = !variant?.onSale; });
      this.querySelectorAll('[data-product-saving]').forEach(node => { node.hidden = true; });
      const contents = this.querySelector('[data-product-contents]');
      if (contents) {
        contents.replaceChildren();
        const list = document.createElement('ul');
        const confirmed = variant?.contents;
        const fallback = (this.text(key === 'b' ? 'contents_b' : 'contents_a', '') || this.dataset.contentsFallback || '').split('\n').filter(Boolean);
        (confirmed?.length ? confirmed : fallback).forEach(text => { const item = document.createElement('li'); item.textContent = text; list.append(item); });
        contents.append(list);
      }
      this.querySelector('[data-product-error]')?.setAttribute('hidden', '');
      this.syncButtons();
      if (updateUrl) {
        const url = new URL(window.location.href); url.searchParams.set('offer', key); window.history.replaceState({}, '', url);
      }
    }
    async refreshOfferProducts() {
      const allTargets = ['a', 'b'].flatMap(key => this.bundleData.mode === 'custom_products' ? this.bundleData[key].slots || [] : [this.bundleData[key]]);
      const activeTargets = this.bundleData.mode === 'custom_products' ? this.bundleData[this.activeBundle].slots || [] : [this.bundleData[this.activeBundle]];
      const unique = new Map(activeTargets.map(product => [String(product.productId), product]));
      const products = await Promise.all([...unique.values()].map(async product => {
        if (typeof product.url !== 'string' || !product.url.startsWith('/') || product.url.startsWith('//') || /[?#]/.test(product.url)) throw new Error(this.text('selection_error', 'Die Auswahl konnte nicht geprüft werden.'));
        let response;
        try { response = await fetch(product.url + '.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } }); }
        catch { throw new Error(this.text('network_error', 'Die Verfügbarkeit konnte nicht geprüft werden. Bitte versuche es erneut.')); }
        if (!response.ok) throw new Error(this.text('network_error', 'Die Verfügbarkeit konnte nicht geprüft werden. Bitte versuche es erneut.'));
        let fresh;
        try { fresh = await response.json(); } catch { throw new Error(this.text('selection_error', 'Die Auswahl konnte nicht geprüft werden.')); }
        if (String(fresh?.id) !== String(product.productId) || !Array.isArray(fresh.variants)) throw new Error(this.text('selection_error', 'Die Auswahl konnte nicht geprüft werden.'));
        return fresh;
      }));
      for (const fresh of products) {
        for (const target of allTargets) {
          if (String(target.productId) !== String(fresh.id)) continue;
          target.variants = target.variants.map(old => {
            const next = fresh.variants.find(value => String(value.id) === String(old.id));
            return next ? { ...old, available: next.available === true, priceAmount: next.price, compareAtAmount: next.compare_at_price || 0, ...(next.quantity_rule ? { min: next.quantity_rule.min, step: next.quantity_rule.increment, max: next.quantity_rule.max } : {}) } : { ...old, available: false };
          });
        }
      }
    }
    selectVariant(id, updateUrl = true) {
      if (this.pending) return;
      const variant = this.variants.find(item => String(item.id) === String(id));
      if (!variant) return;
      this.current = variant;
      if (this.input) this.input.value = String(variant.id);
      this.querySelectorAll('[data-variant-choice]').forEach(input => {input.checked = input.value === String(variant.id);});
      this.querySelectorAll('[data-product-price], [data-sticky-price]').forEach(node => { node.textContent = variant.price; });
      this.querySelectorAll('[data-product-compare]').forEach(node => { node.textContent = variant.compare; node.hidden = !variant.onSale; });
      this.querySelectorAll('[data-product-saving]').forEach(node => { node.textContent = variant.saving; node.hidden = !variant.onSale; });
      const stickyImage = this.querySelector('[data-product-sticky] > img');
      const selectedMedia = [...this.querySelectorAll('[data-media-id]')].find(item => item.dataset.mediaId === String(variant.mediaId));
      const selectedImage = selectedMedia?.querySelector('img');
      if (stickyImage && selectedImage) { stickyImage.src = selectedImage.currentSrc || selectedImage.src; stickyImage.removeAttribute('srcset'); stickyImage.alt = selectedImage.alt; }
      const contents = this.querySelector('[data-product-contents]');
      if (contents) {
        contents.replaceChildren();
        if (variant.contents?.length) {
          const list = document.createElement('ul');
          variant.contents.forEach(text => { const item = document.createElement('li'); item.textContent = text; list.append(item); });
          contents.append(list);
        } else {
          const paragraph = document.createElement('p'); paragraph.textContent = this.dataset.contentsFallback || this.text('contents_fallback', 'Du bestellst die im Kaufbereich ausgewählte Ausführung.'); contents.append(paragraph);
        }
      }
      const quantity = this.querySelector('[data-product-quantity]');
      if (quantity) {
        quantity.min = variant.min;
        quantity.step = variant.step;
        quantity.max = variant.max || '';
        quantity.value = variant.min;
      }
      this.syncButtons();
      this.showMedia(variant.mediaId);
      if (this.activeBundle) this.selectBundle(this.activeBundle);
      if (updateUrl) {
        const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); window.history.replaceState({}, '', url);
      }
      this.querySelector('[data-product-error]')?.setAttribute('hidden', '');
      this.querySelector('[data-product-status]')?.replaceChildren();
      this.dispatchEvent(new CustomEvent('n:variant-change', {bubbles: true, detail: {variant}}));
    }
    showMedia(id) {
      const active = [...this.querySelectorAll('[data-media-id]')].find(media => media.dataset.mediaId === String(id));
      if (!active) return;
      this.querySelectorAll('[data-media-id]').forEach(media => {
        media.hidden = media !== active;
        if (media !== active) media.querySelectorAll('video').forEach(video => video.pause());
      });
      this.querySelectorAll('[data-thumb]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.thumb === String(id))));
    }
    async submit(event) {
      if (!window.NorelviCart?.add) {
        if (this.activeBundle) {
          event.preventDefault();
          const error = this.querySelector('[data-product-error]');
          if (error) { error.hidden = false; error.textContent = this.text('reload', 'Bitte lade die Seite neu und versuche es erneut.'); }
        }
        return;
      }
      event.preventDefault();
      if (this.pending || this.cartUncertain || event.submitter?.disabled || !this.canBuy() || !this.form.reportValidity()) return;
      const error = this.querySelector('[data-product-error]');
      const status = this.querySelector('[data-product-status]');
      if (error) error.hidden = true;
      if (status) status.textContent = '';
      const nativeData = this.activeBundle ? null : new FormData(this.form);
      this.pending = true;
      this.syncButtons();
      let mutationStarted = false;
      try {
        let data;
        if (this.activeBundle) {
          const before = this.resolveBundle(this.activeBundle, this.offerQuantity());
          await this.refreshOfferProducts();
          const current = this.resolveBundle(this.activeBundle, this.offerQuantity());
          if (!current?.available) throw new Error(this.text('stock_error', 'Deine Auswahl ist derzeit nicht verfügbar. Bitte wähle eine andere Ausführung.'));
          if (before.priceAmount !== current.priceAmount) throw new Error(this.text('price_changed', 'Der Preis wurde aktualisiert. Bitte prüfe den Gesamtpreis und bestätige erneut.'));
          data = window.NorelviProductOffers.payload(this.bundleData, this.activeBundle, this.bundleValues(this.activeBundle), this.offerQuantity());
          if (this.activeBundle === 'a' && this.bundleData.mode === 'custom_products') {
            if (!window.NorelviOfferDiscount?.groupItems) throw new Error(this.text('selection_error', 'Die Auswahl konnte nicht geprüft werden.'));
            const token = typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : Array.from(crypto.getRandomValues(new Uint32Array(4))).join('-');
            data.items = window.NorelviOfferDiscount.groupItems(data.items, current.expected, 'a', token);
          }
        } else {
          data = nativeData;
        }
        mutationStarted = true;
        if (this.activeBundle === 'b') {
          if (!window.NorelviCart.addOffer) throw new Error(this.text('offer_unavailable', 'Das Angebot ist gerade nicht verfügbar. Bitte versuche es später erneut.'));
          const current = this.resolveBundle('b', this.offerQuantity());
          await window.NorelviCart.addOffer(data, { ...this.bundleData.discount, expected: current.expected }, event.submitter);
        } else {
          await window.NorelviCart.add(data, event.submitter);
        }
        if (status) status.textContent = this.text('added', 'Zum Warenkorb hinzugefügt.');
      } catch (failure) {
        if (mutationStarted && failure.cartMayHaveChanged !== false) {
          this.cartUncertain = true;
          this.querySelector('[data-product-cart-check]')?.removeAttribute('hidden');
        }
        if (error) {
          error.textContent = mutationStarted && failure.cartMayHaveChanged !== false ? this.text('cart_uncertain', 'Bitte prüfe deinen Warenkorb, bevor du erneut bestellst. Die letzte Anfrage konnte nicht sicher bestätigt werden.') : failure.message || this.text('add_error', 'Das Produkt konnte nicht hinzugefügt werden. Bitte versuche es erneut.');
          error.hidden = false; error.focus({ preventScroll: true });
        }
      } finally {
        this.pending = false;
        if (this.activeBundle) {
          this.bundleVariant = this.resolveBundle(this.activeBundle, this.offerQuantity());
          this.querySelectorAll('[data-product-price], [data-sticky-price]').forEach(node => { node.textContent = this.bundleVariant?.price || this.text('unavailable', 'Nicht verfügbar'); });
          this.querySelectorAll('[data-product-compare]').forEach(node => { node.textContent = this.bundleVariant?.compare || ''; node.hidden = !this.bundleVariant?.onSale; });
          for (const key of ['a', 'b']) { const price = this.querySelector(`[data-bundle-price="${key}"]`); this.paintOfferPrice(price, this.resolveBundle(key)); }
        }
        this.syncButtons();
      }
    }

  });
}
